// Media daily rest constraint
// For each user, ensures at least restMinHours between the last assignment
// of a day D and the first assignment of day D+1 by forbidding pairs of
// assignments whose cross-day gap is shorter than restMinHours.
//
// If gap(a->b) < restMinHours then for each user u:
//   sum_R x_{u,a,R} + sum_R x_{u,b,R} <= 1
//
// Params:
// - restMinHours: number (default 11)
// Requires indexes: users, eventDateYmd, eventStartMs, eventEndMs, staffRequiredByRole, decisionVariables
export function buildMediaDailyRestConstraints(model, params = {}) {
  const idx = model?.optimizationIndexes || {};
  const users = Object.keys(idx.users || {});
  const eventDateYmd = idx.eventDateYmd || {};
  const startMs = idx.eventStartMs || {};
  const endMs = idx.eventEndMs || {};
  const byRole = idx.staffRequiredByRole || {};
  const declared = new Set(Object.keys(idx.decisionVariables || {}));

  const restMinHours = Number(params?.restMinHours) > 0 ? Number(params.restMinHours) : 11;
  const restMinMs = restMinHours * 3600000;

  // Group events by day key ymd
  const byDay = new Map(); // ymd -> [{ eKey, s, e }]
  for (const [eKey, ymd] of Object.entries(eventDateYmd)) {
    const s = startMs[eKey];
    const e = endMs[eKey];
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
    if (!byDay.has(ymd)) byDay.set(ymd, []);
    byDay.get(ymd).push({ eKey, s, e });
  }

  // Helper to compute next day's ymd
  const nextYmd = (ymd) => {
    const [y, m, d] = String(ymd).split('-').map((n) => Number(n));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    const dt = new Date(y, (m - 1), d);
    dt.setDate(dt.getDate() + 1);
    const pad = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  };

  const constraints = [];

  for (const [ymd, listA] of byDay.entries()) {
    const ymdNext = nextYmd(ymd);
    if (!ymdNext || !byDay.has(ymdNext)) continue;
    const listB = byDay.get(ymdNext).slice().sort((a, b) => a.s - b.s);
    const sortedA = listA.slice().sort((a, b) => a.e - b.e);
    for (const a of sortedA) {
      for (const b of listB) {
        const gap = b.s - a.e;
        if (!Number.isFinite(gap)) continue;
        if (gap >= restMinMs) continue; // sufficient rest -> no restriction needed
        // Build pairwise constraint for each user
        for (const uKey of users) {
          const rolesA = Object.keys(byRole[a.eKey] || {});
          const rolesB = Object.keys(byRole[b.eKey] || {});
          const vars = [
            ...rolesA.map((r) => `x_${uKey}_${a.eKey}_${r}`),
            ...rolesB.map((r) => `x_${uKey}_${b.eKey}_${r}`),
          ].filter((name) => declared.has(name));
          if (!vars.length) continue;
          const coeffs = vars.map(() => 1);
          constraints.push({ linear: { vars, coeffs }, domain: [-100000, 1] });
        }
      }
    }
  }

  return constraints;
}

